import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { fetchWithAuth } from "../utils/authFetch";

// Tracks whether the user has an unseen *completed* people search, and whether one
// is running right now.
//
// Find-candidates jobs run in the background. We deliberately do NOT pop the
// user into the results when a search finishes — instead we surface a small
// red notification on the People sidebar item and on the "past searches"
// section of the landing page. The badge clears once the user actually opens
// their past searches.
//
// `activeRun` is the single source of truth for "a search is in flight". It comes
// from the server, not from page state, so it survives remounts, route changes and
// other tabs — which is what lets the UI refuse to start a second search and keep
// showing progress while the user browses their history.
const FindNotificationContext = createContext({
  hasUnseen: false,
  activeRun: null,
  activeRuns: [],
  watchRun: () => {},
  flagFindCompleted: () => {},
  acknowledgeFindResults: () => {},
  reportRun: () => {},
  refreshRun: () => {},
});

export const useFindNotification = () => useContext(FindNotificationContext);

// Idle, nothing polls: we check on mount, when the tab becomes visible, and on
// "activity:changed" (a job was queued, or the shared activity poller woke up).
// While a search is running the dock needs to stay current, so poll on a timer.
const ACTIVE_POLL_INTERVAL_MS = 5 * 1000;
// A page driving its own run feeds us its polls; skip ours while those are fresh.
const REPORT_FRESH_MS = 8 * 1000;
const RUNNING_STATUSES = new Set(["queued", "running"]);

/** The running-job view of a status-by-req-no body, or null when nothing is live. */
function toActiveRun(body) {
  if (!body?.found || !RUNNING_STATUSES.has(body.status)) return null;
  const started = body.startedAt || body.createdAt;
  const startedMs = started ? new Date(started).getTime() : Date.now();
  return {
    jobId: body.jobId || "",
    reqNo: String(body.payload?.req_no || ""),
    title: String(body.payload?.title || "").trim(),
    status: body.status,
    stage: body.stage || "",
    funnel: body.funnel || null,
    // The filters as confirmed at launch; fixed for the run, so sameRun ignores them.
    brief: body.payload?.brief || null,
    country: String(body.payload?.country || "").trim(),
    worldwide: !!body.payload?.worldwide,
    startedAt: Number.isFinite(startedMs) ? startedMs : Date.now(),
  };
}

/** Cheap equality so a poll that changed nothing doesn't re-render every consumer. */
function sameRun(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.jobId === b.jobId &&
    a.status === b.status &&
    a.stage === b.stage &&
    a.startedAt === b.startedAt &&
    JSON.stringify(a.funnel) === JSON.stringify(b.funnel)
  );
}

const seenKeyFor = (sub) => (sub ? `fc-seen-completed:${sub}` : null);

function readSeen(sub) {
  const key = seenKeyFor(sub);
  if (!key || typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSeen(sub, reqNo) {
  const key = seenKeyFor(sub);
  if (!key || !reqNo || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(reqNo));
  } catch {
    // ignore quota / privacy-mode errors
  }
}

export const FindNotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [hasUnseen, setHasUnseen] = useState(false);
  // The run the page is driving or the newest one, with the freshest stage we have for it.
  const [reportedRun, setReportedRun] = useState(null);
  const reportedRunRef = useRef(null);
  const lastReportAtRef = useRef(0);
  // Plans above Starter run several searches at once; this is all of them, newest first.
  const [activeRuns, setActiveRuns] = useState([]);
  const activeRunsSigRef = useRef("");
  // The run the user asked to watch (a click on its progress bar).
  const [preferredReqNo, setPreferredReqNo] = useState("");

  const applyBody = useCallback((body) => {
    const next = toActiveRun(body);
    if (sameRun(reportedRunRef.current, next)) return;
    reportedRunRef.current = next;
    setReportedRun(next);
  }, []);

  const applyActiveList = useCallback((runs) => {
    const list = (Array.isArray(runs) ? runs : []).map(toActiveRun).filter(Boolean);
    const sig = list.map((r) => `${r.reqNo}:${r.status}:${r.stage}`).join("|");
    if (sig === activeRunsSigRef.current) return;
    activeRunsSigRef.current = sig;
    setActiveRuns(list);
  }, []);

  // What the page treats as "the" run: the one being watched, else the one it reported,
  // else the newest. The reported copy wins for that run because its stage is fresher.
  const activeRun = useMemo(() => {
    const wanted = preferredReqNo && activeRuns.find((r) => r.reqNo === preferredReqNo);
    if (wanted) return reportedRun && reportedRun.reqNo === wanted.reqNo ? reportedRun : wanted;
    if (reportedRun && (!activeRuns.length || activeRuns.some((r) => r.reqNo === reportedRun.reqNo))) return reportedRun;
    return activeRuns[0] || reportedRun || null;
  }, [preferredReqNo, activeRuns, reportedRun]);

  const watchRun = useCallback((reqNo) => setPreferredReqNo(String(reqNo || "")), []);

  // Latest observed job (from polling or an explicit flag) so acknowledging
  // records the right req_no as "seen".
  const lastRef = useRef({ reqNo: null, status: null });
  // Becomes true after the first poll so we can suppress badging a search that
  // had already completed before this browser ever loaded (no false positive
  // on first visit), while still catching genuine new completions.
  const initializedRef = useRef(false);

  const userId = user?.sub ?? null;

  const evaluate = useCallback(
    (reqNo, status) => {
      lastRef.current = { reqNo, status };
      if (status !== "succeeded" || !reqNo) {
        initializedRef.current = true;
        return;
      }
      const seen = readSeen(userId);
      if (!initializedRef.current && seen == null) {
        // First-ever observation finds an already-finished search: treat it as
        // seen so we don't badge stale history, but let future searches badge.
        writeSeen(userId, reqNo);
        initializedRef.current = true;
        return;
      }
      initializedRef.current = true;
      if (seen !== reqNo) setHasUnseen(true);
    },
    [userId],
  );

  const pollActive = useCallback(async () => {
    if (!isAuthenticated || !userId) return;
    try {
      const res = await fetchWithAuth("/api/find-candidates/active");
      const body = await res.json().catch(() => ({}));
      if (res.ok) applyActiveList(body?.runs);
    } catch {
      // Next poll will retry.
    }
  }, [isAuthenticated, userId, applyActiveList]);

  const poll = useCallback(async () => {
    if (!isAuthenticated || !userId) return;
    pollActive();
    try {
      const res = await fetchWithAuth("/api/find-candidates/status-by-req-no", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sub: userId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) return;
      applyBody(body);
      if (!body?.found) {
        initializedRef.current = true;
        return;
      }
      evaluate(String(body?.payload?.req_no || ""), body.status);
    } catch {
      // Network blips are non-fatal; the next poll will retry.
    }
  }, [isAuthenticated, userId, evaluate, applyBody, pollActive]);

  /* A page that is driving its own run hands us each poll it makes, so the dock and
     the progress screen never disagree and we don't poll the same job twice. */
  const reportRun = useCallback(
    (body) => {
      lastReportAtRef.current = Date.now();
      applyBody(body);
    },
    [applyBody],
  );

  const refreshRun = useCallback(() => {
    lastReportAtRef.current = 0;
    return poll();
  }, [poll]);

  const isRunning = !!activeRun || activeRuns.length > 0;

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setHasUnseen(false);
      setReportedRun(null);
      reportedRunRef.current = null;
      setActiveRuns([]);
      activeRunsSigRef.current = "";
      initializedRef.current = false;
      lastRef.current = { reqNo: null, status: null };
      return undefined;
    }

    const runPolling = () => {
      if (document.visibilityState !== "visible") return;
      // The page owning this run is already polling it; the list still needs a refresh.
      if (Date.now() - lastReportAtRef.current < REPORT_FRESH_MS) return pollActive();
      poll();
    };

    if (document.visibilityState === "visible") poll();

    const intervalId = isRunning ? setInterval(runPolling, ACTIVE_POLL_INTERVAL_MS) : null;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("activity:changed", runPolling);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("activity:changed", runPolling);
    };
  }, [isAuthenticated, userId, poll, pollActive, isRunning]);

  // Called by the page when its own in-flight search completes, so the badge
  // lights up immediately instead of waiting for the next poll.
  const flagFindCompleted = useCallback((reqNo) => {
    if (reqNo) lastRef.current = { reqNo: String(reqNo), status: "succeeded" };
    initializedRef.current = true;
    // Finished is finished: drop the run now rather than leaving the dock up
    // until the next poll comes back.
    const done = String(reqNo || "");
    if (!done || reportedRunRef.current?.reqNo === done) {
      reportedRunRef.current = null;
      setReportedRun(null);
    }
    if (done) {
      setActiveRuns((list) => {
        const next = list.filter((r) => r.reqNo !== done);
        activeRunsSigRef.current = next.map((r) => `${r.reqNo}:${r.status}:${r.stage}`).join("|");
        return next;
      });
      setPreferredReqNo((cur) => (cur === done ? "" : cur));
    }
    setHasUnseen(true);
  }, []);

  // Called when the user opens their past searches: record the latest
  // completed search as seen and clear the badge.
  const acknowledgeFindResults = useCallback(() => {
    const reqNo = lastRef.current.reqNo;
    if (reqNo) writeSeen(userId, reqNo);
    setHasUnseen(false);
  }, [userId]);

  const value = useMemo(
    () => ({
      hasUnseen,
      activeRun,
      activeRuns,
      watchRun,
      flagFindCompleted,
      acknowledgeFindResults,
      reportRun,
      refreshRun,
    }),
    [
      hasUnseen,
      activeRun,
      activeRuns,
      watchRun,
      flagFindCompleted,
      acknowledgeFindResults,
      reportRun,
      refreshRun,
    ],
  );

  return (
    <FindNotificationContext.Provider value={value}>
      {children}
    </FindNotificationContext.Provider>
  );
};
